import * as s from '~js/utils/selectors';
import * as c from '~js/utils/classes';
import { debounce } from '~js/utils/utils';

export class RangeSlider {
  constructor(container) {
    this.container = container;
    this.init();
    this.initListener = () => this.init();

    document.addEventListener('theme:filters:init', this.initListener);
  }

  init() {
    this.slider = this.container.querySelector(s.selectors$Z.rangeSlider);

    if (!this.slider) {
      return;
    }

    this.resizeFilters = debounce(this.reset.bind(this), 50);

    this.onMoveEvent = (event) => this.onMove(event);
    this.onStopEvent = (event) => this.onStop(event);
    this.onStartEvent = (event) => this.onStart(event);
    this.startX = 0;
    this.x = 0;

    // retrieve touch button
    this.touchLeft = this.slider.querySelector(s.selectors$Z.rangeDotLeft);
    this.touchRight = this.slider.querySelector(s.selectors$Z.rangeDotRight);
    this.lineSpan = this.slider.querySelector(s.selectors$Z.rangeLine);

    // get some properties
    this.min = parseFloat(this.slider.getAttribute(s.selectors$Z.dataMin));
    this.max = parseFloat(this.slider.getAttribute(s.selectors$Z.dataMax));

    this.step = 0.0;

    // normalize flag
    this.normalizeFact = 20;

    // retrieve default values
    let defaultMinValue = this.min;
    if (this.slider.hasAttribute(s.selectors$Z.dataMinValue)) {
      defaultMinValue = parseFloat(this.slider.getAttribute(s.selectors$Z.dataMinValue));
    }
    let defaultMaxValue = this.max;

    if (this.slider.hasAttribute(s.selectors$Z.dataMaxValue)) {
      defaultMaxValue = parseFloat(this.slider.getAttribute(s.selectors$Z.dataMaxValue));
    }

    // check values are correct
    if (defaultMinValue < this.min) {
      defaultMinValue = this.min;
    }

    if (defaultMaxValue > this.max) {
      defaultMaxValue = this.max;
    }

    if (defaultMinValue > defaultMaxValue) {
      defaultMinValue = defaultMaxValue;
    }

    if (this.slider.getAttribute(s.selectors$Z.dataStep)) {
      this.step = Math.abs(parseFloat(this.slider.getAttribute(s.selectors$Z.dataStep)));
    }

    // initial reset
    this.reset();
    window.addEventListener('theme:resize', this.resizeFilters);

    // usefull values, min, max, normalize fact is the width of both touch buttons
    this.maxX = this.slider.offsetWidth - this.touchRight.offsetWidth;
    this.selectedTouch = null;
    this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;

    // set defualt values
    this.setMinValue(defaultMinValue);
    this.setMaxValue(defaultMaxValue);

    // link events
    this.touchLeft.addEventListener('mousedown', this.onStartEvent);
    this.touchRight.addEventListener('mousedown', this.onStartEvent);
    this.touchLeft.addEventListener('touchstart', this.onStartEvent, { passive: true });
    this.touchRight.addEventListener('touchstart', this.onStartEvent, { passive: true });

    // initialize
    this.slider.classList.add(c.classes$O.isInitialized);
  }

  reset() {
    this.touchLeft.style.left = '0px';
    this.touchRight.style.left = this.slider.offsetWidth - this.touchLeft.offsetWidth + 'px';
    this.lineSpan.style.marginLeft = '0px';
    this.lineSpan.style.width = this.slider.offsetWidth - this.touchLeft.offsetWidth + 'px';
    this.startX = 0;
    this.x = 0;

    this.maxX = this.slider.offsetWidth - this.touchRight.offsetWidth;
    this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;
  }

  setMinValue(minValue) {
    const ratio = (minValue - this.min) / (this.max - this.min);
    this.touchLeft.style.left =
      Math.ceil(ratio * (this.slider.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact))) + 'px';
    this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
    this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';
    this.slider.setAttribute(s.selectors$Z.dataMinValue, minValue);
  }

  setMaxValue(maxValue) {
    const ratio = (maxValue - this.min) / (this.max - this.min);
    this.touchRight.style.left =
      Math.ceil(
        ratio * (this.slider.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact)) + this.normalizeFact
      ) + 'px';
    this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
    this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';
    this.slider.setAttribute(s.selectors$Z.dataMaxValue, maxValue);
  }

  onStart(event) {
    // Prevent default dragging of selected content
    event.preventDefault();
    let eventTouch = event;

    if (event.touches) {
      eventTouch = event.touches[0];
    }

    if (event.currentTarget === this.touchLeft) {
      this.x = this.touchLeft.offsetLeft;
    } else if (event.currentTarget === this.touchRight) {
      this.x = this.touchRight.offsetLeft;
    }

    this.startX = eventTouch.pageX - this.x;
    this.selectedTouch = event.currentTarget;
    document.addEventListener('mousemove', this.onMoveEvent);
    document.addEventListener('mouseup', this.onStopEvent);
    document.addEventListener('touchmove', this.onMoveEvent, { passive: true });
    document.addEventListener('touchend', this.onStopEvent, { passive: true });
  }

  onMove(event) {
    let eventTouch = event;

    if (event.touches) {
      eventTouch = event.touches[0];
    }

    this.x = eventTouch.pageX - this.startX;

    if (this.selectedTouch === this.touchLeft) {
      if (this.x > this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10) {
        this.x = this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10;
      } else if (this.x < 0) {
        this.x = 0;
      }

      this.selectedTouch.style.left = this.x + 'px';
    } else if (this.selectedTouch === this.touchRight) {
      if (this.x < this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10) {
        this.x = this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10;
      } else if (this.x > this.maxX) {
        this.x = this.maxX;
      }
      this.selectedTouch.style.left = this.x + 'px';
    }

    // update line span
    this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
    this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';

    // write new value
    this.calculateValue();

    // call on change
    if (this.slider.getAttribute('on-change')) {
      const fn = new Function('min, max', this.slider.getAttribute('on-change'));
      fn(this.slider.getAttribute(s.selectors$Z.dataMinValue), this.slider.getAttribute(s.selectors$Z.dataMaxValue));
    }

    this.onChange(
      this.slider.getAttribute(s.selectors$Z.dataMinValue),
      this.slider.getAttribute(s.selectors$Z.dataMaxValue)
    );
  }

  onStop(event) {
    document.removeEventListener('mousemove', this.onMoveEvent);
    document.removeEventListener('mouseup', this.onStopEvent);
    document.removeEventListener('touchmove', this.onMoveEvent, { passive: true });
    document.removeEventListener('touchend', this.onStopEvent, { passive: true });

    this.selectedTouch = null;

    // write new value
    this.calculateValue();

    // call did changed
    this.onChanged(
      this.slider.getAttribute(s.selectors$Z.dataMinValue),
      this.slider.getAttribute(s.selectors$Z.dataMaxValue)
    );
  }

  onChange(min, max) {
    const rangeHolder = this.slider.closest(s.selectors$Z.rangeHolder);
    if (rangeHolder) {
      const priceMin = rangeHolder.querySelector(s.selectors$Z.priceMin);
      const priceMax = rangeHolder.querySelector(s.selectors$Z.priceMax);

      if (priceMin && priceMax) {
        priceMin.value = parseInt(min);
        priceMax.value = parseInt(max);
      }
    }
  }

  onChanged(min, max) {
    if (this.slider.hasAttribute(s.selectors$Z.dataFilterUpdate)) {
      this.slider.dispatchEvent(new CustomEvent('theme:filter:range-update', { bubbles: true }));
    }
  }

  calculateValue() {
    const newValue = (this.lineSpan.offsetWidth - this.normalizeFact) / this.initialValue;
    let minValue = this.lineSpan.offsetLeft / this.initialValue;
    let maxValue = minValue + newValue;

    minValue = minValue * (this.max - this.min) + this.min;
    maxValue = maxValue * (this.max - this.min) + this.min;

    if (this.step !== 0.0) {
      let multi = Math.floor(minValue / this.step);
      minValue = this.step * multi;

      multi = Math.floor(maxValue / this.step);
      maxValue = this.step * multi;
    }

    if (this.selectedTouch === this.touchLeft) {
      this.slider.setAttribute(s.selectors$Z.dataMinValue, minValue);
    }

    if (this.selectedTouch === this.touchRight) {
      this.slider.setAttribute(s.selectors$Z.dataMaxValue, maxValue);
    }
  }

  unload() {
    document.removeEventListener('theme:filters:init', this.initListener);
    window.removeEventListener('theme:resize', this.resizeFilters);
  }
}